import {
  AlertOutlined,
  BarChartOutlined,
  ClusterOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  ExperimentOutlined,
  ProfileOutlined,
  SyncOutlined,
  ToolOutlined,
} from '@ant-design/icons';

import {
  ANALYTICS_TAB_VISIT,
  FLEET_TAB_VISIT,
  INSPECTIONS_TAB_VISIT,
  TASKS_TAB_VISIT,
  WORK_ORDERS_TAB_VISIT,
} from 'utils/access-control/rules';
import { useAccountContext } from 'utils/account/AccountContext';
import { useFeatures } from 'utils/features';
import { SIDEBAR_IMPROVEMENTS } from 'utils/release-toggles';
import { notEmpty } from 'utils/types';
import { usePermissions } from 'utils/usePermissions';

import { CMS as CMSIcon, Finance, Performance, Solar } from './customIcons';
import { type Tab } from './types';

export function useNavTabs() {
  const {
    WORK_ORDERS,
    SISENSE,
    ALERT_MANAGER,
    DEVICE_FIRMWARE_MANAGEMENT,
    HORIZON_DAMAGES,
    LEGACY_DAMAGES,
    CMS,
    FINANCE,
    PERFORMANCE,
    SOLAR,
    UPLOADS,
  } = useFeatures().features;

  const { hasReleaseToggle } = useAccountContext();
  const hasNewTabsRT = hasReleaseToggle(SIDEBAR_IMPROVEMENTS);

  const { canAccess } = usePermissions();

  const inspectionsLink: string = HORIZON_DAMAGES
    ? '/damages2'
    : LEGACY_DAMAGES
      ? '/damages'
      : '/inspections';

  const tabs: Tab[] = [
    // this is a menu item to test release toggles
    hasReleaseToggle('test_rt')
      ? {
          title: 'Release Toggles',
          icon: <ExperimentOutlined />,
          link: '/',
          id: 'sidebar-rt-button',
          rules: [true],
        }
      : undefined,
    {
      title: 'Fleet',
      icon: <EnvironmentOutlined />,
      link: '/fleet',
      subRoutes: ['/assets'],
      id: 'sidebar-fleet-button',
      rules: [FLEET_TAB_VISIT],
    },
    {
      title: 'Inspections',
      icon: <ProfileOutlined />,
      link: inspectionsLink,
      subRoutes: [
        '/inspections',
        '/inspection-uploads',
        ...(LEGACY_DAMAGES ? ['/damages'] : []),
        ...(HORIZON_DAMAGES ? ['/damages2'] : []),
      ],
      id: 'sidebar-inspections-button',
      rules: [INSPECTIONS_TAB_VISIT],
    },
    {
      title: 'Reports',
      icon: <DownloadOutlined />,
      link: '/reports',
      subRoutes: ['/report-deliveries'],
      id: 'sidebar-reports-button',
      rules: [true], // shorthand for anyone with the feature flag can view
    },
    WORK_ORDERS
      ? {
          title: 'Work Orders',
          icon: <ToolOutlined />,
          link: '/work-orders',
          subRoutes: ['/tasks', '/unplanned-tasks', '/campaigns'],
          id: 'sidebar-work-orders-button',
          rules: [WORK_ORDERS_TAB_VISIT],
        }
      : {
          title: 'Tasks',
          icon: <ToolOutlined />,
          link: '/tasks',
          id: 'sidebar-tasks-button',
          rules: [TASKS_TAB_VISIT],
        },
    SISENSE
      ? {
          title: 'Analytics',
          icon: <BarChartOutlined />,
          link: '/analytics',
          id: 'sidebar-analytics-button',
          rules: [ANALYTICS_TAB_VISIT],
        }
      : undefined,
    ALERT_MANAGER
      ? {
          title: 'Alerts',
          icon: <AlertOutlined />,
          link: '/alerts',
          id: 'sidebar-alerts-button',
          rules: [true], // shorthand for anyone with the feature flag can view
        }
      : undefined,
    DEVICE_FIRMWARE_MANAGEMENT
      ? {
          title: 'Device Firmware Management',
          icon: <ClusterOutlined />,
          link: '/device-firmware-management',
          id: 'sidebar-device-firmwares-button',
          rules: [true], // all with feature flag can access
        }
      : undefined,
    ...(hasNewTabsRT
      ? [
          {
            title: 'CMS',
            icon: <CMSIcon />,
            link: 'https://cms.skyspecs.com',
            id: 'sidebar-cms-button',
            rules: [true],
            target: '_blank',
            disabled: !CMS,
            disabledTooltip: (
              <>
                Learn more about{' '}
                <a href="https://skyspecs.com/cms-software/" target="_blank" rel="noreferrer">
                  SkySpecs' CMS offering
                </a>
              </>
            ),
          },
          {
            title: 'Performance',
            icon: <Performance />,
            noLinkTooltip: 'Please navigate to your unique reports dashboard',
            id: 'sidebar-performance-button',
            rules: [true],
            target: '_blank',
            disabled: !PERFORMANCE,
            disabledTooltip: (
              <>
                Learn more about{' '}
                <a
                  href="https://skyspecs.com/performance-monitoring/"
                  target="_blank"
                  rel="noreferrer"
                >
                  SkySpecs' Performance offering
                </a>
              </>
            ),
          },
          {
            title: 'Finance',
            icon: <Finance />,
            link: 'https://fam.fincovi.com/#/',
            id: 'sidebar-finance-button',
            rules: [true],
            target: '_blank',
            disabled: !FINANCE,
            disabledTooltip: (
              <>
                Learn more about{' '}
                <a
                  href="https://skyspecs.com/services/financial-asset-management/"
                  target="_blank"
                  rel="noreferrer"
                >
                  SkySpecs' Finance offering
                </a>
              </>
            ),
          },
          {
            title: 'Solar',
            icon: <Solar />,
            link: 'https://sam.skyspecs.com/',
            id: 'sidebar-solar-button',
            rules: [true],
            target: '_blank',
            disabled: !SOLAR,
            disabledTooltip: (
              <>
                Learn more about{' '}
                <a href="https://skyspecs.com/horizon-solar/" target="_blank" rel="noreferrer">
                  SkySpecs' Solar offering
                </a>
              </>
            ),
          },
        ]
      : []),
    UPLOADS
      ? {
          title: 'Uploads',
          icon: <SyncOutlined />,
          link: '/uploads',
          id: 'sidebar-uploads-button',
          rules: [true],
        }
      : undefined,
  ]
    .filter(notEmpty)
    .filter(({ rules }) => canAccess(rules));

  return tabs;
}
